import { QueryClientContext, QueryKey } from "@tanstack/react-query";
import { useCallback, useContext, useState } from "react";

/**
 * Re-runs the mounted (`type: "active"`) queries whose key starts with one of
 * `queryKeys` — the table's own data, not every query the app has open — and
 * reports whether that run is still in flight. The promise resolves once all
 * of them have settled, which is exactly how long the table's "Refreshing…"
 * treatment should last.
 *
 * Keys match by prefix, so `["admin-alerts"]` covers `["admin-alerts", id]`.
 * With no keys, or outside a `QueryClientProvider` (a bare unit render of a
 * table wrapper, say), `isAvailable` is false and `refresh` is a no-op.
 */
export function useRefreshQueries(queryKeys: readonly QueryKey[] = []) {
  const queryClient = useContext(QueryClientContext);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Callers pass an inline array literal every render; its contents are what
  // matters, so the serialised form keys the callback.
  const keysSignature = JSON.stringify(queryKeys);

  const refresh = useCallback(async () => {
    const keys = JSON.parse(keysSignature) as QueryKey[];
    if (!queryClient || keys.length === 0) return;
    setIsRefreshing(true);
    try {
      await Promise.all(
        keys.map((queryKey) =>
          queryClient.refetchQueries({ queryKey, type: "active" })
        )
      );
    } finally {
      setIsRefreshing(false);
    }
  }, [queryClient, keysSignature]);

  return {
    isAvailable: Boolean(queryClient) && queryKeys.length > 0,
    isRefreshing,
    refresh,
  };
}
